import { CrmFieldService, StandardIds } from '@galaxy/crm/services/field-service';
import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  WritableSignal,
  signal,
  Signal,
  computed,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ActivityTaskStatus, SimplifiedUser } from '../../../tokens-and-interfaces';
import { TranslationModule } from '../../../i18n/translation-module';
import { ActivityService } from '../../../shared-services';
import { ActivityInterface, FieldMaskInterface, ActivityAssociationInterface } from '@vendasta/crm';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { User } from '@vendasta/iamv2';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { CollapsibleSectionComponent } from '../../collapsible-section/collapsible-section.component';
import { SanitizeHtmlPipe } from './sanitize-html.pipe';
import { CrmObjectIdentifier } from './activity.interface';

@Component({
  selector: 'crm-activity-task',
  templateUrl: './task.component.html',
  styleUrls: ['./task.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    MatInputModule,
    MatIconModule,
    CollapsibleSectionComponent,
    SanitizeHtmlPipe,
  ],
})
export class CrmActivityTaskComponent implements OnInit {
  completedTaskControl = new FormControl<boolean>(false);
  dateFormat = DateFormat.mediumDateShortTime;
  private _activity: WritableSignal<ActivityInterface> = signal({});
  @Input({ required: true }) set activity(activity: ActivityInterface) {
    this._activity.set(activity);
  }
  get activity() {
    return this._activity();
  }
  @Output() activityChange = new EventEmitter<ActivityInterface>();

  @Input() users?: User[] | SimplifiedUser[] | null;

  @Input() associatedObject?: CrmObjectIdentifier | null;

  private unknownNamePlaceholder = 'CONTACT.ACTIVITIES.UNKNOWN_NAME';

  cardInfo: Signal<ActivityCardInformation> = computed(() => {
    return {
      crmObjectId: this._activity().crmObjectId,
      title: this.getFieldData(this._activity(), StandardIds.ActivityName),
      body: this.getFieldData(this._activity(), StandardIds.ActivityTaskBody),
      status: this.getFieldData(this._activity(), StandardIds.ActivityTaskStatus),
      type: this.getFieldData(this._activity(), StandardIds.ActivityTaskType),
      priority: this.getFieldData(this._activity(), StandardIds.ActivityTaskPriority),
      dueDate:
        this.fieldService.getFieldValueFromCrmObject(this._activity(), StandardIds.ActivityTaskDueDate)?.dateValue ??
        null,
      assignee: this.getAssigneeName(this._activity()),
      associations: this.getAssociations(this._activity()),
    };
  });

  ngOnInit(): void {
    const taskIsCompleted =
      this.activity?.fields?.find((field) => field.fieldId === StandardIds.ActivityTaskStatus)?.stringValue ===
      ActivityTaskStatus.Completed;
    this.completedTaskControl.setValue(taskIsCompleted, { emitEvent: false });
  }

  private getFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }

  private getAssigneeName(activity: ActivityInterface): string {
    const ownerId = activity?.ownerId ?? '';
    const user = this.users?.find((user) => user.userId === ownerId);
    const userName = [user?.firstName, user?.lastName]
      .map((name) => name?.trim())
      .filter((name) => name)
      .join(' ');
    return userName || this.unknownNamePlaceholder;
  }

  private getTaskCompletionStatus(): string {
    return this.completedTaskControl.value ? ActivityTaskStatus.Completed : ActivityTaskStatus.Open;
  }

  private getAssociations(activity: ActivityInterface): ActivityAssociationInterface[] {
    return activity?.associations ?? [];
  }

  private emitStatusChange(status: string): void {
    const activity = this.activity;
    const field = this.fieldService.getFieldValueFromCrmObject(activity, StandardIds.ActivityTaskStatus);
    if (field) {
      field.stringValue = status;
    } else {
      activity.fields?.push({ fieldId: StandardIds.ActivityTaskStatus, stringValue: status });
    }
    this._activity.set(activity);
    this.activityChange.emit(activity);
  }

  async updateTaskCompletionStatus(): Promise<void> {
    const status = this.getTaskCompletionStatus();
    const activity: ActivityInterface = {
      crmObjectId: this.activity.crmObjectId,
      crmObjectSubtype: this.activity.crmObjectSubtype,
      fields: [{ fieldId: StandardIds.ActivityTaskStatus, stringValue: status }],
    };

    const mask: FieldMaskInterface = {
      paths: [StandardIds.ActivityTaskStatus],
    };
    try {
      await firstValueFrom(this.activityService.updateActivity(activity, mask));
      this.snack.openSuccessSnack('TASK.STATUS_UPDATE.SUCCESS');
      this.emitStatusChange(status);
    } catch {
      this.snack.openErrorSnack('TASK.STATUS_UPDATE.ERROR');
    }
  }

  constructor(
    private readonly fieldService: CrmFieldService,
    private readonly activityService: ActivityService,
    private readonly snack: SnackbarService,
  ) {}
}
interface ActivityCardInformation {
  title: string;
  body: string;
  status: string;
  type: string;
  priority: string;
  dueDate: Date | null;
  assignee: string;
  associations: ActivityAssociationInterface[];
}
